import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import {
    Alert,
    alpha,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import Popup from "../../../components/Popup";
import {
    downloadPromotionReport,
    fetchActiveStudentsForRollover,
    fetchRolloverPreview,
    markTermDepartures,
    runYearPromotion,
    TERM_DEPARTURE_OPTIONS,
} from "../../../utils/schoolRolloverApi";

const SchoolRollover = () => {
    const { currentUser } = useSelector((state) => state.user);
    const schoolId = currentUser?._id || currentUser?.id;

    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [preview, setPreview] = useState(null);
    const [activeStudents, setActiveStudents] = useState([]);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [departureStatus, setDepartureStatus] = useState("Left");
    const [departureNote, setDepartureNote] = useState("");
    const [reviewYear, setReviewYear] = useState(String(new Date().getFullYear()));
    const [newAcademicYear, setNewAcademicYear] = useState(String(new Date().getFullYear() + 1));
    const [saving, setSaving] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [message, setMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);

    const loadAll = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            const [previewData, activeData] = await Promise.all([
                fetchRolloverPreview(schoolId, { academicYear: reviewYear.trim() }),
                fetchActiveStudentsForRollover(schoolId),
            ]);
            setPreview(previewData);
            setActiveStudents(Array.isArray(activeData.students) ? activeData.students : []);
        } catch (e) {
            setMessage(e.response?.data?.message || e.message || "Could not load rollover data.");
            setShowPopup(true);
        } finally {
            setLoading(false);
        }
    }, [schoolId, reviewYear]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const toggleStudent = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        if (selectedIds.size === activeStudents.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(activeStudents.map((s) => s._id || s.id)));
        }
    };

    const handleTermDepartures = async () => {
        if (!selectedIds.size) {
            setMessage("Select at least one student who will not return next term.");
            setShowPopup(true);
            return;
        }
        setSaving(true);
        try {
            const result = await markTermDepartures(schoolId, {
                studentIds: [...selectedIds],
                status: departureStatus,
                note: departureNote,
            });
            setMessage(result.message || "Students updated.");
            setShowPopup(true);
            setSelectedIds(new Set());
            setDepartureNote("");
            await loadAll();
        } catch (e) {
            setMessage(e.response?.data?.message || e.message || "Could not save.");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    const handleYearPromotion = async (force = false) => {
        if (preview?.summary?.issues > 0 && !force) {
            setMessage("Create missing classes shown in the issues table before running year promotion.");
            setShowPopup(true);
            return;
        }
        if (preview?.summary?.rollConflicts > 0 && !force) {
            setMessage("Resolve roll number conflicts first, or confirm force promotion.");
            setShowPopup(true);
            return;
        }
        const ok = window.confirm(
            `Move ${preview?.summary?.toPromote ?? 0} students up one class, keep ${preview?.summary?.toRepeat ?? 0} repeating, and graduate ${preview?.summary?.toGraduate ?? 0}? This cannot be undone easily.`,
        );
        if (!ok) return;

        setSaving(true);
        try {
            const result = await runYearPromotion(schoolId, {
                academicYear: newAcademicYear.trim(),
                reviewYear: reviewYear.trim(),
                force,
            });
            setMessage(result.message || "Year promotion completed.");
            setShowPopup(true);
            await loadAll();
        } catch (e) {
            setMessage(e.response?.data?.message || e.message || "Year promotion failed.");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    const handleDownloadReport = async () => {
        setExporting(true);
        try {
            await downloadPromotionReport(schoolId, { academicYear: reviewYear.trim() });
            setMessage("Promotion report downloaded.");
            setShowPopup(true);
        } catch (e) {
            setMessage(e.response?.data?.message || e.message || "Could not export report.");
            setShowPopup(true);
        } finally {
            setExporting(false);
        }
    };

    const summary = preview?.summary;

    const promoteRows = useMemo(() => preview?.promote || [], [preview]);
    const repeatRows = useMemo(() => preview?.repeat || [], [preview]);
    const graduateRows = useMemo(() => preview?.graduate || [], [preview]);
    const issueRows = useMemo(() => preview?.issues || [], [preview]);

    if (loading && !preview) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[50]} 24%)`
                        : alpha(t.palette.common.black, 0.08),
            }}
        >
            <Container maxWidth="lg">
                <Stack spacing={2} sx={{ mb: 3 }}>
                    <Typography variant="h4" fontWeight={800}>
                        Term & year rollover
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
                        At <strong>term end</strong>, mark pupils who will not come back (transferred, expelled, or
                        otherwise not returning). At <strong>year end</strong>, eligible pupils move up one class;
                        pupils who fail promotion rules repeat the same class. Std 8 can progress to Form 1 in combined
                        schools; Std 8 and Form 4 finish school. CA (40%) + exam (60%) feeds automated pass/fail
                        suggestions.
                    </Typography>
                </Stack>

                <Paper sx={{ borderRadius: 3, mb: 2 }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}>
                        <Tab icon={<EventBusyOutlinedIcon />} iconPosition="start" label="End of term" />
                        <Tab icon={<TrendingUpOutlinedIcon />} iconPosition="start" label="End of year" />
                    </Tabs>

                    {tab === 0 && (
                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                                Selected students are marked as not returning. They stay on record but will not be
                                promoted and cannot use the student portal.
                            </Alert>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
                                <FormControl fullWidth sx={{ maxWidth: 360 }}>
                                    <InputLabel>Reason</InputLabel>
                                    <Select
                                        label="Reason"
                                        value={departureStatus}
                                        onChange={(e) => setDepartureStatus(e.target.value)}
                                    >
                                        {TERM_DEPARTURE_OPTIONS.map((o) => (
                                            <MenuItem key={o.value} value={o.value}>
                                                {o.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    fullWidth
                                    label="Note (optional)"
                                    value={departureNote}
                                    onChange={(e) => setDepartureNote(e.target.value)}
                                    placeholder="e.g. Transferred to ABC Primary"
                                />
                            </Stack>
                            <TableContainer sx={{ maxHeight: 420, mb: 2 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    indeterminate={
                                                        selectedIds.size > 0 &&
                                                        selectedIds.size < activeStudents.length
                                                    }
                                                    checked={
                                                        activeStudents.length > 0 &&
                                                        selectedIds.size === activeStudents.length
                                                    }
                                                    onChange={toggleAll}
                                                />
                                            </TableCell>
                                            <TableCell>Roll</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Class</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {activeStudents.map((s) => {
                                            const id = s._id || s.id;
                                            return (
                                                <TableRow key={id} hover selected={selectedIds.has(id)}>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            checked={selectedIds.has(id)}
                                                            onChange={() => toggleStudent(id)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{s.rollNum}</TableCell>
                                                    <TableCell>{s.name}</TableCell>
                                                    <TableCell>{s.className}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Button
                                variant="contained"
                                color="warning"
                                disabled={saving || selectedIds.size === 0}
                                onClick={handleTermDepartures}
                            >
                                {saving ? "Saving…" : `Mark ${selectedIds.size || ""} as not returning`}
                            </Button>
                        </Box>
                    )}

                    {tab === 1 && (
                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            {summary ? (
                                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                                    <Chip label={`${summary.toPromote} promote`} color="primary" />
                                    {summary.toRepeat > 0 ? (
                                        <Chip label={`${summary.toRepeat} repeat`} color="warning" />
                                    ) : null}
                                    <Chip label={`${summary.toGraduate} graduate`} color="success" />
                                    <Chip label={`${summary.skipped} skipped`} variant="outlined" />
                                    {summary.promotionRate != null ? (
                                        <Chip
                                            label={`Promotion rate ${summary.promotionRate}%`}
                                            variant="outlined"
                                            color="primary"
                                        />
                                    ) : null}
                                    {summary.repetitionRate != null && summary.toRepeat > 0 ? (
                                        <Chip
                                            label={`Repetition ${summary.repetitionRate}%`}
                                            variant="outlined"
                                            color="warning"
                                        />
                                    ) : null}
                                    {summary.retentionRate != null ? (
                                        <Chip
                                            label={`Retention ${summary.retentionRate}%`}
                                            variant="outlined"
                                        />
                                    ) : null}
                                    {summary.issues > 0 ? (
                                        <Chip label={`${summary.issues} issues`} color="error" />
                                    ) : null}
                                    {summary.rollConflicts > 0 ? (
                                        <Chip label={`${summary.rollConflicts} roll conflicts`} color="warning" />
                                    ) : null}
                                </Stack>
                            ) : null}

                            {(preview?.reminders || []).map((r) => (
                                <Alert key={r} severity="info" sx={{ mb: 1, borderRadius: 2 }}>
                                    {r}
                                </Alert>
                            ))}

                            {preview?.entryClasses?.length ? (
                                <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} icon={<SchoolOutlinedIcon />}>
                                    Entry classes for new pupils:{" "}
                                    {preview.entryClasses.map((c) => c.name).join(", ")} — use{" "}
                                    <Button component={RouterLink} to="/Admin/addstudents" size="small">
                                        Add student
                                    </Button>{" "}
                                    after promotion.
                                </Alert>
                            ) : null}

                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
                                <TextField
                                    label="Closing academic year (term pass/fail)"
                                    value={reviewYear}
                                    onChange={(e) => setReviewYear(e.target.value)}
                                    helperText="Primary term reviews for this year, e.g. 2025"
                                    sx={{ maxWidth: 320 }}
                                />
                                <TextField
                                    label="New academic year label (optional)"
                                    value={newAcademicYear}
                                    onChange={(e) => setNewAcademicYear(e.target.value)}
                                    helperText="Updates all class registers after promotion, e.g. 2026"
                                    sx={{ maxWidth: 320 }}
                                />
                            </Stack>

                            {issueRows.length > 0 ? (
                                <>
                                    <Typography variant="subtitle1" fontWeight={700} color="error" gutterBottom>
                                        Fix before promotion
                                    </Typography>
                                    <TableContainer sx={{ mb: 2 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Student</TableCell>
                                                    <TableCell>Issue</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {issueRows.map((row) => (
                                                    <TableRow key={row.studentId}>
                                                        <TableCell>{row.name}</TableCell>
                                                        <TableCell>{row.message}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            ) : null}

                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                Promotion preview
                            </Typography>
                            <TableContainer sx={{ maxHeight: 280, mb: 2 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>From</TableCell>
                                            <TableCell>To</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {promoteRows.map((row) => (
                                            <TableRow key={row.studentId}>
                                                <TableCell>
                                                    {row.name}
                                                    {row.rollConflict ? (
                                                        <Chip size="small" label="Roll clash" color="warning" sx={{ ml: 1 }} />
                                                    ) : null}
                                                </TableCell>
                                                <TableCell>{row.fromClassName}</TableCell>
                                                <TableCell>{row.toClassName}</TableCell>
                                            </TableRow>
                                        ))}
                                        {repeatRows.map((row) => (
                                            <TableRow key={`repeat-${row.studentId}`}>
                                                <TableCell>{row.name}</TableCell>
                                                <TableCell>{row.fromClassName}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label="Repeat same class" color="warning" />
                                                    {row.reason ? (
                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                            {row.reason}
                                                        </Typography>
                                                    ) : null}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {graduateRows.map((row) => (
                                            <TableRow key={row.studentId}>
                                                <TableCell>{row.name}</TableCell>
                                                <TableCell>{row.fromClassName}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label="Graduated" color="success" />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    disabled={saving || (!summary?.toPromote && !summary?.toGraduate && !summary?.toRepeat)}
                                    onClick={() => handleYearPromotion(false)}
                                    sx={{ fontWeight: 700 }}
                                >
                                    {saving ? "Running…" : "Run year-end promotion"}
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<DownloadOutlinedIcon />}
                                    disabled={exporting || loading}
                                    onClick={handleDownloadReport}
                                >
                                    {exporting ? "Exporting…" : "Download promotion report"}
                                </Button>
                                <Button variant="outlined" onClick={loadAll} disabled={loading}>
                                    Refresh preview
                                </Button>
                            </Stack>
                        </Box>
                    )}
                </Paper>
            </Container>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
        </Box>
    );
};

export default SchoolRollover;
